import { useState } from "react";
import { CompanionWeapon, CompanionWeaponBlock, CompanionWeaponBlockBase } from "../../../model/character";
import { v4 } from 'uuid';

import { Box, Button, Chip, Divider, FormGroup, Stack, styled, TextField, Typography } from "@mui/material";
import { NumberField } from "../../../components/formFields/NumberField";
import StatLookupField from "../../../components/StatLookupField";
import FormFieldLabel from "../../../components/formFields/FormFieldLabel";
import { ModalAction, ModalContentSection } from "../../../components/Modal";
import SelectField from "../../../components/formFields/SelectField";
import ButtonFlat, { ButtonFlatIcon } from "../../../components/ButtonFlat";

import RemoveIcon from '@mui/icons-material/Remove';
import { useNavigate, useParams } from "react-router-dom";
import { exportData, loadLibraryData, saveLibraryData } from "../../../master/libraryData";
import AbilityEditor from "../../characterPage/AbilityEditor";
import Page from "../../../components/Page";
import { CreateDefaultCombatSkills } from "../../../master/data";

const WeaponBlockList = styled('div')({
    display: "flex",
    height: "33px"
})

const WeaponBlockListLabel = styled('div')({
    flexGrow: 1,
    paddingLeft: "8px",
    display: "flex",
})

const BlockListButton = styled('button')(({ theme }) => ({
    background: "unset",
    border: 0,
    outline: 0,
    cursor: "pointer",
    padding: 0,
    display: "inline",
    color: theme.palette.text.primary,
    fontSize: theme.typography.body1.fontSize,
    
    "&:hover": {
        textDecoration: "underline"
    }
}));

const CompanionWeaponBlockEditor = (props: {
        weaponBlock: CompanionWeaponBlock | CompanionWeaponBlockBase,
        setWeaponBlock: (block: CompanionWeaponBlock | CompanionWeaponBlockBase) => void}        
    ) => {
    const { weaponBlock: companionWeaponBlock, setWeaponBlock: setCompanionWeaponBlock } = props;
    const [ feature, setFeature ] = useState("");

    const onChange = (key: string, value: any) => {
        setCompanionWeaponBlock({
            ...companionWeaponBlock,
            [key]: value
        })
    }

    const handleAddFeature = () => {
        const newFeatures = [ ...companionWeaponBlock.addFeatures, feature ];
        onChange("addFeatures", newFeatures);
        setFeature("");
    }

    const handleRemoveFeature = (index: number) => {
        const features = [ ...companionWeaponBlock.addFeatures ];
        features.splice(index, 1);
        onChange("addFeatures", features);
    }

    return <>
        <Stack spacing={1}>
            <FormFieldLabel label="Name">
                <TextField fullWidth type="text" variant="standard" value={companionWeaponBlock.name} onChange={(event) => onChange("name", event.currentTarget.value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Hit Dice" justifyContent="end">
                <NumberField step={1} min={0} value={companionWeaponBlock.hitDice} onChange={(value) => onChange("hitDice", value)} customRenderer={(val) => `${val}d6`}/>
            </FormFieldLabel>
            <FormFieldLabel label="Hit Bonus" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.hitBonus} onChange={(value) => onChange("hitBonus", value)} />
            </FormFieldLabel>
            {'skillBonusId' in companionWeaponBlock &&
            <FormFieldLabel label="Skill Bonus" justifyContent="end"><StatLookupField onChange={(value) => onChange("skillBonusId", value)} value={companionWeaponBlock.skillBonusId} stats={[
                                ...CreateDefaultCombatSkills(),
                            ]} /></FormFieldLabel> }
            <FormFieldLabel label="Range" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.range} onChange={(value) => onChange("range", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Endurance Damage" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.enduranceDamage} onChange={(value) => onChange("enduranceDamage", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Crit Damage" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.critDamage} onChange={(value) => onChange("critDamage", value)} />
            </FormFieldLabel>
            <SelectField value={companionWeaponBlock.size} onChange={(value) => onChange("size", value)} options={[
                { name: "Size - Trinket (0.25)", value: "trinket" },
                { name: "Size - Small (1)", value: "small" } ,
                { name: "Size - Standard (2)", value: "standard" } ,
                { name: "Size - Large (3)", value: "large" } ,
            ]} />
            <SelectField value={companionWeaponBlock.carried} onChange={(value) => onChange("carried", value)} options={[
                { name: "Carried on person", value: "none" },
                { name: "Carried by companion.", value: "companion" } ,
                { name: "Stored (Spacecaft)", value: "stored" }
            ]} />
            <FormFieldLabel label="Equipment Slots" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.slots} onChange={(value) => onChange("slots", value)} />
            </FormFieldLabel>
            
            <FormFieldLabel label="Hands" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.hands} onChange={(value) => onChange("hands", value)}  />
            </FormFieldLabel>
            <FormFieldLabel label="Draw" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.draw} onChange={(value) => onChange("draw", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Strong Hit Range" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.strongHitRange} onChange={(value) => onChange("strongHitRange", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Reload" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.reload} onChange={(value) => onChange("reload", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Defence" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.defence} onChange={(value) => onChange("defence", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Armour" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.armour} onChange={(value) => onChange("armour", value)}/>
            </FormFieldLabel>
            <FormFieldLabel label="Durability" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.durability} onChange={(value) => onChange("durability", value)}/>
            </FormFieldLabel>
            <FormFieldLabel label="Bodies" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.bodies} onChange={(value) => onChange("bodies", value)}/>
            </FormFieldLabel>
            <FormFieldLabel label="Mobility" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.mobility} onChange={(value) => onChange("mobility", value)}/>
            </FormFieldLabel>
            <FormFieldLabel label="Acquire" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.acquire} onChange={(value) => onChange("acquire", value)}/>
            </FormFieldLabel>
            <FormFieldLabel label="Influence" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.influence} onChange={(value) => onChange("influence", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Resources" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.resources} onChange={(value) => onChange("resources", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Knowledge" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.knowledge} onChange={(value) => onChange("knowledge", value)} customRenderer={(v) => `${v}kn`} />
            </FormFieldLabel>
        </Stack>
        <ModalContentSection title="Keywords">
            <Stack direction="row">
                <TextField sx={{flexGrow: 1}} type="text" variant="standard" value={feature} onChange={(event) => setFeature(event.currentTarget.value)} />
                <Button onClick={handleAddFeature}>Add Feature</Button>
            </Stack>
            <Stack flexWrap="wrap" useFlexGap direction="row" spacing={1}>
                {companionWeaponBlock.addFeatures.map((value, index) => <Chip onClick={() => handleRemoveFeature(index)} label={value} key={index} />)}
            </Stack>
        </ModalContentSection>
        <AbilityEditor title="Passive Abilities" abilities={companionWeaponBlock.passives} setAbilities={(value) => onChange("passives", value)} />
        <Box paddingLeft={1}>
            <Typography>Notes</Typography>
            <TextField value={companionWeaponBlock.notes} onChange={(event) => onChange('notes', event.currentTarget.value)} multiline fullWidth />
        </Box>
        <AbilityEditor title="Strong Hit Options" abilities={companionWeaponBlock.strongHits} setAbilities={(value) => onChange("strongHits", value)} />
    </>
}

const defaultBlock: CompanionWeaponBlock = {
    id: "",
    name: "Companion",
    slots: 0,
    acquire: 0,
    critDamage: 0,
    enduranceDamage: 0,
    hitDice: 0,
    hitBonus: 0,
    range: 0,
    resources: 0,
    knowledge: 0,
    influence: 0,
    weaponType: [],
    addFeatures: [],
    removeFeatures: [],
    strongHits: [],
    passives: [],
    draw: 0,
    reload: 0,
    strongHitRange: 0,
    carried: "none",
    hands: 1,
    size: "standard",
    notes: "",
    armour: 0,
    bodies: 0,
    defence: 0,
    durability: 0,
    mobility: 0,
}

const defaultWeapon: CompanionWeapon = {
    base: {
        id: "",
        name: "Base Companion",
        slots: 2,
        acquire: 0,
        critDamage: 0,
        enduranceDamage: 0,
        hitDice: 0,
        hitBonus: 0,
        range: 0,
        resources: 0,
        knowledge: 0,
        influence: 0,
        weaponType: [],
        addFeatures: [],
        removeFeatures: [],
        passives: [],
        strongHits: [],
        skillBonusId: "",
        draw: 1,
        reload: 2,
        strongHitRange: 1,
        carried: "none",
        size: "standard",
        hands: 1,
        notes: "",
        armour: 0,
        bodies: 1,
        defence: 1,
        durability: 1,
        mobility: 1,
    },
    variations: [],
    modifications: [],
    id: "",
    isAcquired: false,
    isHeld: false,
    isStashed: false,
    name: "New Companion (Weapon)",
    description: "",
    munitionsSpent: 0,
    reloadsPerformed: 0,
}

export const GetNewCompanion = (): CompanionWeapon => {
    return {
        ...defaultWeapon,
        id: v4()
    }
}

export const CompanionWeaponEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ current, setCurrent ] = useState<CompanionWeapon>(loadLibraryData<CompanionWeapon>("lcomwep", id ?? "NONE") ?? GetNewCompanion());
    const [ currentVariation, setCurrentVariation ] = useState<number | undefined>(undefined);
    const [ currentModification, setCurrentModification ] = useState<number | undefined>(undefined);

    const handleSave = () => {
        navigate(`/library/companionWeapon/${current.id}`, {replace: true});
        saveLibraryData("lcomwep", current);
    }

    const handleBack = () => {
        navigate("/library");
    }

    const handleExport = () => {
        exportData(current, "lcomwep");
    }

    const handleAddVariation = () => {
        const variations = [ ...current.variations, { ...defaultBlock, name: "New Variation" } ]
        setCurrent((c) => ({
            ...c,
            variations
        }));
    }

    const handleRemoveVariation = (index: number) => {
        const variations = [ ...current.variations ];
        variations.splice(index,1);
        setCurrent((c) => ({
            ...c,
            variations
        }));
    }

    const handleChangeVariation = (index: number, variation: CompanionWeaponBlock) => {
        const variations = [ ...current.variations ];
        variations[index] = variation;
        setCurrent((c) => ({
            ...c,
            variations
        }));
    }

    const handleAddModification = () => {
        const modifications = [ ...current.modifications, { ...defaultBlock, name: "New Modification" } ]
        setCurrent((c) => ({
            ...c,
            modifications
        }));
    }

    const handleRemoveModification = (index: number) => {
        const modifications = [ ...current.modifications ];
        modifications.splice(index,1);
        setCurrent((c) => ({
            ...c,
            modifications
        }));
    }

    const handleChangeModification = (index: number, modification: CompanionWeaponBlock) => {
        const modifications = [ ...current.modifications ];
        modifications[index] = modification;
        setCurrent((c) => ({
            ...c,
            modifications
        }));
    }
    
    const onChange = (key: string, value: any) => {
        setCurrent((c) => ({
            ...c,
            [key]: value
        }))
    }

    const actionList: ModalAction[] = [];
    if(currentModification !== undefined || currentVariation !== undefined) {
        actionList.push({
            label: "Return",
            onClick: () => { setCurrentModification(undefined); setCurrentVariation(undefined); },
            size: 12
        })
    }
    actionList.push({
        label: "Cancel",    
        onClick: () => undefined,
    });
    actionList.push({
        label: "Save",    
        onClick: handleSave,
        primary: true,
    });

    return <Page>
            <h2>{current.name}</h2>
            <FormGroup>
                <FormFieldLabel label="Name">
                    <TextField fullWidth type="text" variant="standard" value={current.name} onChange={(event) => onChange("name", event.currentTarget.value)} />
                </FormFieldLabel>
                <ModalContentSection title="Type and Variation" onAdd={handleAddVariation} collapsable>
                    {current.variations.map((variation, index) => {
                        return <WeaponBlockList>
                            <WeaponBlockListLabel><BlockListButton onClick={() => { setCurrentVariation(index); setCurrentModification(undefined); }}>{variation.name}</BlockListButton></WeaponBlockListLabel>
                            <ButtonFlatIcon onClick={() => handleRemoveVariation(index)}><RemoveIcon /></ButtonFlatIcon>
                        </WeaponBlockList>
                    })}
                </ModalContentSection>
                <ModalContentSection title="Modifications" onAdd={handleAddModification} collapsable>
                    {current.modifications.map((modification, index) => {
                        return <WeaponBlockList>
                            <WeaponBlockListLabel><BlockListButton onClick={() => { setCurrentModification(index); setCurrentVariation(undefined); }}>{modification.name}</BlockListButton></WeaponBlockListLabel>
                            <ButtonFlatIcon onClick={() => handleRemoveModification(index)}><RemoveIcon /></ButtonFlatIcon>
                        </WeaponBlockList>
                    })}
                </ModalContentSection>
                <Divider sx={{ marginY: 2}}/>

                {currentVariation !== undefined && <CompanionWeaponBlockEditor weaponBlock={current.variations[currentVariation]} setWeaponBlock={(wb) => handleChangeVariation(currentVariation, wb)} />}
                {currentModification !== undefined && <CompanionWeaponBlockEditor weaponBlock={current.modifications[currentModification]} setWeaponBlock={(wb) => handleChangeModification(currentModification, wb)} />}
                {currentModification === undefined && currentVariation === undefined && <CompanionWeaponBlockEditor weaponBlock={current.base} setWeaponBlock={(wb) => onChange("base", wb)} />}

                <Box paddingLeft={1}>
                    <Typography>Description</Typography>
                    <TextField value={current.description} onChange={(event) => onChange('description', event.currentTarget.value)} multiline fullWidth />
                </Box>

                <Stack direction={"row"} spacing={1} paddingTop={1}>
                    {(currentModification !== undefined || currentVariation !== undefined) && <ButtonFlat onClick={() => { setCurrentModification(undefined); setCurrentVariation(undefined); }}>Return</ButtonFlat>}
                    <ButtonFlat onClick={handleSave}>Save</ButtonFlat>
                    <ButtonFlat onClick={handleBack}>Back to Library</ButtonFlat>
                    <ButtonFlat onClick={handleExport}>Export</ButtonFlat>
                </Stack>
            </FormGroup>
        </Page>
}